/**
 * ArFollowups — full list page at /ar/followups.
 * Filter by status, assignee=me|all, overdue-only.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { ListChecks, Check, Trash, ArrowSquareOut, WarningCircle, ArrowsCounterClockwise, X } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { listArFollowups, updateArFollowup, deleteArFollowup } from '../lib/api';

function relTime(iso) {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const m = Math.floor(abs / 60000);
  if (m < 60) return `${ms < 0 ? '-' : ''}${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${ms < 0 ? '-' : ''}${h}h`;
  const d = Math.floor(h / 24);
  return `${ms < 0 ? '-' : ''}${d}d`;
}

const STATUS_CHIP = {
  open: 'bg-violet-50 border-violet-300 text-violet-700',
  done: 'bg-emerald-50 border-emerald-300 text-emerald-700',
  cancelled: 'bg-zinc-50 border-zinc-300 text-zinc-500',
};

export default function ArFollowups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');
  const [assignee, setAssignee] = useState('me');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await listArFollowups({
        status, assignee,
        overdue_only: overdueOnly ? 'true' : '',
      });
      setItems(r.data?.items || []);
    } catch (_) { toast.error('Could not load follow-ups'); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [status, assignee, overdueOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRowStatus = async (t, newStatus) => {
    try {
      await updateArFollowup(t.id, { status: newStatus });
      toast.success(newStatus === 'done' ? 'Marked done' : newStatus === 'cancelled' ? 'Cancelled' : 'Reopened');
      await refresh();
    } catch (_) { toast.error('Could not update'); }
  };

  const handleDelete = async () => {
    const t = confirmDelete;
    setConfirmDelete(null);
    if (!t) return;
    try {
      await deleteArFollowup(t.id);
      toast.success('Follow-up deleted');
      await refresh();
    } catch (_) { toast.error('Could not delete'); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="ar-followups-page">
        <div className="flex items-center gap-2">
          <ListChecks size={18} weight="bold" className="text-violet-600" />
          <h1 className="text-2xl font-bold tracking-tight">AR Follow-ups</h1>
        </div>
        <div className="bg-white border border-zinc-200 p-3 flex flex-wrap gap-2 items-center">
          {['open', 'done', 'cancelled', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-2 py-1 text-[11px] font-bold uppercase border ${
                status === s ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-zinc-300 hover:bg-zinc-50'
              }`}
              data-testid={`ar-status-${s}`}
            >{s}</button>
          ))}
          <span className="mx-2 h-5 w-px bg-zinc-200" />
          {['me', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setAssignee(s)}
              className={`px-2 py-1 text-[11px] font-bold uppercase border ${
                assignee === s ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-zinc-300 hover:bg-zinc-50'
              }`}
              data-testid={`ar-assignee-${s}`}
            >{s === 'me' ? 'Mine' : 'Everyone'}</button>
          ))}
          <label className="ml-2 inline-flex items-center gap-1.5 px-2 py-1 border border-zinc-200 cursor-pointer hover:bg-zinc-50">
            <input type="checkbox" checked={overdueOnly} onChange={(e) => setOverdueOnly(e.target.checked)} data-testid="ar-overdue-only" />
            <span className="text-xs">Overdue only</span>
          </label>
          <Button onClick={refresh} variant="outline" size="sm" data-testid="ar-refresh">
            <ArrowsCounterClockwise size={12} className="mr-1" /> Refresh
          </Button>
          <span className="ml-auto text-[11px] text-zinc-500">{items.length} task{items.length === 1 ? '' : 's'}</span>
        </div>

        <div className="bg-white border border-zinc-200">
          {loading && items.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-400 italic">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-xs text-zinc-400 italic" data-testid="ar-followups-empty">No follow-ups match.</div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {items.map((t) => {
                const overdue = t.status === 'open' && new Date(t.due_date).getTime() < Date.now();
                return (
                  <li key={t.id} className="px-4 py-3 flex items-start gap-3" data-testid={`ar-followup-row-${t.id}`}>
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border shrink-0 ${STATUS_CHIP[t.status]}`}>{t.status}</span>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-sm truncate">{t.title}</div>
                      <div className="text-[11px] text-zinc-500 flex flex-wrap gap-2">
                        <span className={overdue ? 'text-rose-700 font-bold' : ''}>
                          {overdue && <WarningCircle size={10} weight="bold" className="inline mr-0.5" />}
                          Due {new Date(t.due_date).toLocaleString()} · {relTime(t.due_date)}
                        </span>
                        {t.assignee_name && <span>· {t.assignee_name}</span>}
                        {t.customer_email && (
                          <Link to={`/invoices/customer/${encodeURIComponent(t.customer_email)}`} className="text-violet-700 hover:underline">
                            {t.customer_name || t.customer_email}
                          </Link>
                        )}
                        {t.invoice_number && <span className="font-mono">{t.invoice_number}</span>}
                      </div>
                      {t.notes && <div className="text-[11px] text-zinc-600 italic mt-1">"{t.notes}"</div>}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {t.status === 'open' ? (
                        <>
                          <Button size="sm" variant="outline" onClick={() => setRowStatus(t, 'done')} data-testid={`ar-row-done-${t.id}`}>
                            <Check size={12} className="mr-1" /> Done
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setRowStatus(t, 'cancelled')} data-testid={`ar-row-cancel-${t.id}`}>
                            <X size={12} />
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => setRowStatus(t, 'open')} data-testid={`ar-row-reopen-${t.id}`}>
                          Reopen
                        </Button>
                      )}
                      {t.customer_email && (
                        <Link to={`/invoices/customer/${encodeURIComponent(t.customer_email)}`}
                              className="inline-flex items-center justify-center w-7 h-7 border border-zinc-300 hover:bg-zinc-50"
                              title="Open customer"
                              data-testid={`ar-row-open-${t.id}`}>
                          <ArrowSquareOut size={12} />
                        </Link>
                      )}
                      <button onClick={() => setConfirmDelete(t)} className="inline-flex items-center justify-center w-7 h-7 border border-zinc-300 hover:bg-rose-50 hover:border-rose-300" data-testid={`ar-row-delete-${t.id}`}>
                        <Trash size={12} className="text-rose-600" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="ar-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this follow-up?</AlertDialogTitle>
            <AlertDialogDescription>"{confirmDelete?.title}" will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="ar-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
